import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAuthStore, useStudioStore } from '@/store'
import { getSupabase } from '@/lib/supabase'
import { withAuthRecovery } from '@/lib/authRecovery'

type PedagogyAccessRole = 'advanced' | 'pedagogy_lead' | 'pedagogy_specialist' | null

/**
 * Результат резолва доступа — ТРИ состояния, а не два:
 *   `undefined` — ещё проверяем;
 *   `null`      — роли действительно нет;
 *   `'error'`   — проверить не смогли (сеть, протухший JWT, таймаут).
 *
 * До 21.08.2026 состояний было два: любая ошибка сводилась к `null`, то есть
 * обрыв сети воспроизводился как ОТЗЫВ ПРАВ — человека молча выбрасывало на
 * `/management/hub`, и он шёл к администратору с вопросом «почему у меня
 * забрали доступ». В мобильном WebView это не редкость
 * (AUDIT_PEDAGOGY_UX_2026-08-21.md, P0 «гейт трактует сбой как „прав нет“»).
 */
type PedagogyAccessResolution = PedagogyAccessRole | 'error'

/**
 * Кэш разрешённой роли на пользователя.
 *
 * Хук зовут и layout-маршрут, и сами страницы (на карточке специалиста — трижды),
 * а до 21.08.2026 каждый вызов заново дёргал RPC и на время ответа показывал
 * «Проверяем доступ…». Кэшируется ТОЛЬКО успешный ответ: закэшировать `'error'`
 * значило бы запереть человека вне раздела до перезагрузки страницы, а именно
 * от этого и защищает отдельное состояние ошибки.
 */
let cachedUserId: string | null = null
let cachedRole: PedagogyAccessRole | undefined = undefined
let inFlight: { userId: string; promise: Promise<PedagogyAccessResolution> } | null = null

function resolvePedagogyAccessRole(userId: string): Promise<PedagogyAccessResolution> {
  if (inFlight && inFlight.userId === userId) return inFlight.promise

  const supabase = getSupabase()
  const promise = withAuthRecovery(() => supabase.rpc('pedagogy_teacher_registry_access_role'))
    .then(({ data, error }): PedagogyAccessResolution => {
      if (error) {
        console.error('Failed to resolve pedagogy access:', error)
        return 'error'
      }

      const role = (data as PedagogyAccessRole) ?? null
      cachedUserId = userId
      cachedRole = role
      return role
    })
    .catch((error): PedagogyAccessResolution => {
      console.error('Failed to resolve pedagogy access:', error)
      return 'error'
    })
    .finally(() => {
      if (inFlight?.promise === promise) inFlight = null
    })

  inFlight = { userId, promise }
  return promise
}

/**
 * Единая проверка доступа к педагогическому разделу.
 *
 * Источник истины — серверная функция pedagogy_teacher_registry_access_role(),
 * поэтому UI учитывает scheduled pedagogy_role_end_date так же, как RLS/RPC.
 */
export function usePedagogyAccess() {
  const { user } = useAuthStore()
  const { studios } = useStudioStore()
  const userId = user?.id ?? null
  const [accessRole, setAccessRole] = useState<PedagogyAccessResolution | undefined>(
    () => (userId && cachedUserId === userId ? cachedRole : undefined)
  )
  // Счётчик попыток: кнопка «Повторить» меняет его и перезапускает эффект.
  const [attempt, setAttempt] = useState(0)

  const isManagementCompany = useMemo(
    () => studios.some(
      studio => studio.access_kind === 'management_company' || studio.role === 'management_company'
    ),
    [studios]
  )

  useEffect(() => {
    let cancelled = false

    if (!userId) {
      cachedUserId = null
      cachedRole = undefined
      setAccessRole(null)
      return
    }

    if (cachedUserId === userId && cachedRole !== undefined) {
      setAccessRole(cachedRole)
      return
    }

    setAccessRole(undefined)

    void resolvePedagogyAccessRole(userId).then(role => {
      if (!cancelled) setAccessRole(role)
    })

    return () => {
      cancelled = true
    }
  }, [userId, attempt])

  const retryAccessCheck = useCallback(() => {
    // Сбрасываем и незавершённый запрос: иначе повтор подпишется на тот же
    // промис, который уже упал, и кнопка окажется декоративной.
    inFlight = null
    setAttempt(value => value + 1)
  }, [])

  const accessError = accessRole === 'error'
  const isAdvanced = accessRole === 'advanced'
  const isPedagogyLead = accessRole === 'pedagogy_lead'
  const isPedagogySpecialist = accessRole === 'pedagogy_specialist'
  const canManagePedagogy = isAdvanced || isPedagogyLead
  const canViewAllPedagogy = canManagePedagogy
  // Не смогли проверить — это НЕ «доступ есть», но и не «доступа нет»:
  // решение принимает вызывающий, разводя редирект и экран-объяснение.
  const canViewPedagogySection = accessRole != null && !accessError

  return {
    loading: Boolean(userId) && accessRole === undefined,
    accessError,
    retryAccessCheck,
    accessLevel: isAdvanced ? 'advanced' as const : (accessRole && !accessError) ? 'standard' as const : null,
    jobTitle: isPedagogyLead ? 'pedagogy_lead' : isPedagogySpecialist ? 'pedagogy_specialist' : null,
    isManagementCompany,
    isAdvanced,
    isPedagogyLead,
    isPedagogySpecialist,
    canManagePedagogy,
    canViewAllPedagogy,
    canViewPedagogySection,
  }
}
